import React from "react";
import { Link } from "react-router-dom";
import {
  archiveProjects,
  disciplines,
  featuredProjects,
} from "../data/portfolio";
import P5Backdrop from "../components/P5Backdrop";
import ProjectCard from "../components/ProjectCard";
import {
  Arrow,
  NORTH_EAST_ARROW,
  PageMeta,
} from "../components/SiteChrome";

export default function HomePage() {
  return (
    <>
      <PageMeta description="Clément Girault works across digital strategy, service design, software, AI, photography, moving image and sound." />
      <section className="home-hero">
        <div className="hero-copy">
          <span className="eyebrow">Digital strategist · Service designer · Maker</span>
          <h1>
            Ideas into
            <br />
            systems into
            <br />
            <span>experiences.</span>
          </h1>
          <p>
            I work across strategy, software, neuro-symbolic AI, photography,
            moving image and sound—bringing complex ideas into useful, vivid
            form.
          </p>
          <div className="hero-actions">
            <Link className="button button-dark" to="/work">
              View work <Arrow />
            </Link>
            <a
              className="button button-outline"
              href="/cv"
              target="_blank"
              rel="noreferrer"
            >
              View CV
              <span className="sr-only"> (opens in a new tab)</span>
              <Arrow />
            </a>
          </div>
        </div>
        <div className="hero-collage" aria-label="Selected photography and digital work">
          <P5Backdrop variant="threads" palette="cream" />
          <div className="collage-shape collage-yellow" />
          <div className="collage-shape collage-cobalt" />
          <figure className="hero-photo">
            <Link
              className="hero-photo-link"
              to="/work/engine-80"
              aria-label="View the Engine 80 project"
            >
              <img
                src="/works/applications/engine80/E801.png"
                alt="Engine 80 branching dialogue interface"
              />
              <span className="hero-photo-caption">
                Engine 80 · View project {NORTH_EAST_ARROW}
              </span>
            </Link>
          </figure>
          <div className="hero-note">
            <span>In development</span>
            <strong>A creative engine.</strong>
            <small>Mac · iPhone · iPad</small>
          </div>
        </div>
      </section>

      <div className="discipline-ticker" aria-label="Areas of practice">
        <div>
          {[...disciplines, ...disciplines].map((discipline, index) => (
            <React.Fragment key={`${discipline}-${index}`}>
              <span>{discipline}</span>
              <i aria-hidden="true">✦</i>
            </React.Fragment>
          ))}
        </div>
      </div>

      <section className="featured-work section-pad">
        <div className="section-heading">
          <div>
            <span className="eyebrow">01 · Selected work</span>
            <h2>Four threads.<br />One practice.</h2>
          </div>
          <Link className="text-link" to="/work">
            See the full archive <Arrow />
          </Link>
        </div>
        <div className="featured-grid">
          {featuredProjects.map((project, index) => (
            <ProjectCard
              project={project}
              featured={index === 0 || index === 3}
              displayTitle={project.homeTitle}
              key={project.slug}
            />
          ))}
        </div>
      </section>

      <section className="practice-split">
        <div className="practice-panel practice-panel-cobalt">
          <P5Backdrop variant="orbit" palette="cobalt" />
          <span className="eyebrow">02 · The practice</span>
          <h2>From principle<br />to practice.</h2>
          <p>
            Twenty-five years across government, education, research and
            consulting—connecting strategy, experience design and hands-on
            delivery.
          </p>
          <Link className="button button-light" to="/about">
            Read profile <Arrow />
          </Link>
        </div>
        <div className="practice-list">
          {[
            ["Strategy + transformation", "Lead"],
            ["Service + experience design", "Shape"],
            ["Software + neuro-symbolic AI", "Build"],
            ["Image + sound", "Make"],
          ].map(([label, action], index) => (
            <div key={label}>
              <span>{String(index + 1).padStart(2, "0")}</span>
              <strong>{label}</strong>
              <em>{action}</em>
            </div>
          ))}
          <blockquote>
            The useful work usually happens where disciplines overlap.
          </blockquote>
        </div>
      </section>

      <section className="archive-strip section-pad">
        <div className="section-heading compact">
          <div>
            <span className="eyebrow">03 · More from the archive</span>
            <h2>Experiments, tools and recordings.</h2>
          </div>
          <Link className="text-link" to="/work">
            See all work <Arrow />
          </Link>
        </div>
        <div className="archive-row">
          {archiveProjects.map((project) => (
            <ProjectCard
              project={project}
              displayTitle={project.homeTitle}
              key={project.slug}
            />
          ))}
        </div>
      </section>
    </>
  );
}
